export type NavMenuItem = {
  label: string;
  href: string;
  external?: boolean;
  description?: string;
  iconSrc?: string;
  iconAlt?: string;
};

export type NavLink = {
  label: string;
  href: string;
  external?: boolean;
};

export const MARKETING_NAV_LINKS = {
  brand: { href: '/', label: 'KudosWall Pro' },
  resume: [
    {
      label: 'AI Resume Builder',
      href: '/',
      description: 'Allows users to create resumes using AI.',
      iconSrc: '/common-assets/images/resumes-icon.svg',
      iconAlt: 'Resume Icon',
    },
    {
      label: 'Europass CV Builder',
      href: '/europass-cv-builder',
      description: 'Helps users generate resumes efficiently using AI.',
      iconSrc: '/common-assets/images/euro-cv.svg',
      iconAlt: 'Europass CV',
    },
    {
      label: 'Resume Analyzer',
      href: '/resume-analyzer',
      description: 'Analyzes resumes for improvements and optimizations.',
      iconSrc: '/common-assets/images/cv-analyze.svg',
      iconAlt: 'Resume Analyzer',
    },
    {
      label: 'Job Fit Checker',
      href: '/job-fit-checker',
      description: 'Checks if your resume matches the job description.',
      iconSrc: '/common-assets/images/cv-analyze.svg',
      iconAlt: 'Job Fit Checker',
    },
    {
      label: 'Salary Insights',
      href: '/salary-insights-report',
      description: 'AI-Powered Salary Insights & Career Report',
      iconSrc: '/common-assets/images/salary-icon.svg',
      iconAlt: 'Salary Insights',
    },
    {
      label: 'Resume Templates',
      href: '/resume-templates',
      description: 'ATS-Friendly Resume Templates to Land Your Next Job',
      iconSrc: '/common-assets/images/resume-template-icon.svg',
      iconAlt: 'Resume Templates',
    },
  ] satisfies NavMenuItem[],
  topLevel: [
    { label: 'Interview Prep', href: '/interview-preparation' },
    { label: 'Cover Letter Generator', href: '/coverletter' },
    { label: 'LinkedIn Optimizer', href: '/linkedin' },
  ] satisfies NavLink[],
  resources: [
    {
      label: 'Resume Examples',
      href: '/resume-examples',
      external: true,
      description: 'Explore professionally crafted resume examples to inspire your own.',
      iconSrc: '/common-assets/images/resumes-icon.svg',
      iconAlt: 'resume-icon',
    },
    {
      label: 'Cover Letter Examples',
      href: '/cover-letter-examples',
      external: true,
      description: 'Discover cover letter examples that make a strong first impression.',
      iconSrc: '/common-assets/images/coverletter-icon.svg',
      iconAlt: 'cover letter icon',
    },
    {
      label: 'Our Blog',
      href: '/blog',
      description: 'Read expert career tips and job search insights on our blog.',
      iconSrc: '/common-assets/images/blog-icon.svg',
      iconAlt: 'blog icon',
    },
    {
      label: 'Resume Writing Guide',
      href: '/guides/how-to-make-a-resume',
      external: true,
      description: 'Learn how to craft a compelling resume that stands out.',
      iconSrc: '/common-assets/images/writing-guide-icon.svg',
      iconAlt: 'writing guide icon',
    },
    {
      label: 'ATS Resume Score Guide',
      href: '/guides/ats-resume-score-guide',
      description: 'Optimize your resume to pass Applicant Tracking Systems (ATS).',
      iconSrc: '/common-assets/images/ats-icon.svg',
      iconAlt: 'ATS icon',
    },
    {
      label: 'More Guides',
      href: '/guides',
      external: true,
      description: 'Explore in-depth career guides to boost your job search.',
      iconSrc: '/common-assets/images/more-guide-icon.svg',
      iconAlt: 'guides icon',
    },
    {
      label: 'Help',
      href: 'https://kudoswall.tawk.help',
      external: true,
      description: 'Get answers to your questions and support when you need it.',
      iconSrc: '/common-assets/images/help-icon.svg',
      iconAlt: 'help icon',
    },
  ] satisfies NavMenuItem[],
  signIn: { href: '/login', label: 'Sign In' },
  footer: {
    company: [
      { label: 'About', href: '/about' },
      { label: 'Terms', href: '/terms-of-service' },
      { label: 'Privacy', href: '/privacy-policy' },
      { label: 'Security', href: '/security' },
      { label: 'Blog', href: '/blog' },
    ] satisfies NavLink[],
    social: [
      { label: 'Facebook', href: 'https://www.facebook.com/kudoswall', external: true },
      { label: 'Twitter', href: 'https://twitter.com/kudoswall', external: true },
      {
        label: 'LinkedIn',
        href: 'https://www.linkedin.com/company/kudoswall/',
        external: true,
      },
      { label: 'Instagram', href: 'https://www.instagram.com/kudoswall/', external: true },
      { label: 'Telegram', href: 'https://t.me/careerprep', external: true },
    ] satisfies NavLink[],
    support: [{ label: 'Contact Us', href: 'mailto:hi@kudoswall.com' }] satisfies NavLink[],
  },
} as const;

export function getAllInternalNavHrefs(): string[] {
  const hrefs = new Set<string>();
  const add = (href: string, external?: boolean) => {
    if (!external && href.startsWith('/')) {
      hrefs.add(href);
    }
  };

  add(MARKETING_NAV_LINKS.brand.href);
  MARKETING_NAV_LINKS.resume.forEach((item) => add(item.href));
  MARKETING_NAV_LINKS.topLevel.forEach((item) => add(item.href));
  MARKETING_NAV_LINKS.resources.forEach((item: NavMenuItem) => add(item.href, item.external));
  add(MARKETING_NAV_LINKS.signIn.href);
  MARKETING_NAV_LINKS.footer.company.forEach((item: NavLink) => add(item.href, item.external));

  return [...hrefs].sort();
}
